import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Response, apiAbortController } from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import { SicCode, Employee } from './types';
import { ApplicationScheme } from '../../hoc/common/types';
import api from '../../api/api';

export const clientsDictionariesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCustomersDictionaries: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/incorporation-dates?all=true'),
        ]);

        return dictionaries
          ? { data: { incorporationDates: dictionaries[0].data } }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getCustomersCASP: build.query<Response<CASP>, void>({
      query: () => ({
        url: '/v1/casps?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersSettings: build.query<
      Response<{ key: string; data: { value: boolean } }>,
      void
    >({
      query: () => ({
        url: '/v1/settings?take=9999',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersInfoDictionaries: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/settings-clients-ranges?all=true'),
          fetchWithBQ('/v1/delivery-channels?all=true&clientType=INDIVIDUAL'),
          fetchWithBQ('/v1/delivery-channels?all=true&clientType=CORPORATE'),
          fetchWithBQ('/v1/products?all=true&clientType=INDIVIDUAL'),
          fetchWithBQ('/v1/products?all=true&clientType=CORPORATE'),
          fetchWithBQ('/v1/sources-of-funds?all=true'),
          fetchWithBQ('/v1/clients-activities?all=true'),
          fetchWithBQ('/v1/sic-codes?all=true'),
          fetchWithBQ('/v1/transactions-types?all=true'),
          fetchWithBQ('/v1/risk-score-ranges?all=true'),
          fetchWithBQ('/v1/psp-accounts?all=true'),
        ]);

        return dictionaries
          ? {
              data: {
                ranges: dictionaries[0].data,
                deliveryChannelsIndividual: dictionaries[1].data,
                deliveryChannelsCorporate: dictionaries[2].data,
                productsIndividual: dictionaries[3].data,
                productsCorporate: dictionaries[4].data,
                sourcesOfFunds: dictionaries[5].data,
                clientsActivities: dictionaries[6].data,
                sicCodes: dictionaries[7].data,
                transactionTypes: dictionaries[8].data,
                riskScoreRanges: dictionaries[9].data,
                pspAccounts: dictionaries[10].data,
              },
            }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getCustomersSicCodes: build.query<Response<SicCode>, string>({
      query: (query) => ({
        url: `/v1/sic-codes${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersApplicationSchemes: build.query<
      Response<ApplicationScheme>,
      void
    >({
      query: () => ({
        url: `/v1/applications-schemes?all=true&active=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersEmployeesList: build.query<Response<Employee>, void>({
      query: () => ({
        url: '/v1/employees?take=9999',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersEmployee: build.query<{ data: Employee }, string>({
      query: (id) => ({
        url: `/v1/employees/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerEmployee: build.query<{ data: Employee }, void>({
      query: () => ({
        url: '/v1/employees/whoami',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetCustomersDictionariesQuery,
  useGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersSicCodesQuery,
  useLazyGetCustomersEmployeesListQuery,
  useLazyGetCustomersEmployeeQuery,
  useLazyGetCustomerEmployeeQuery,
  useLazyGetCustomersApplicationSchemesQuery,
  useLazyGetCustomersSettingsQuery,
  useLazyGetCustomersCASPQuery,
} = clientsDictionariesApi;

export const {
  endpoints: {
    getCustomersDictionaries,
    getCustomersSicCodes,
    getCustomersInfoDictionaries,
    getCustomersEmployeesList,
    getCustomersEmployee,
    getCustomerEmployee,
    getCustomersApplicationSchemes,
    getCustomersSettings,
    getCustomersCASP,
  },
} = clientsDictionariesApi;
